import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
const maxWidthDependsOnSize = {
    small: 'max-w-[400px]',
    medium: 'max-w-[600px]',
    large: 'max-w-[700px]',
    default: 'max-w-[900px]'
};
const SearchableSelectContext = /*#__PURE__*/ React.createContext(null);
export function useSearchableSelect() {
    const context = React.useContext(SearchableSelectContext);
    if (!context) {
        throw new Error('useSearchableSelect must be used within SearchableSelect');
    }
    return context;
}
export function SearchableSelectOption(param) {
    let { value, children, className } = param;
    const { handleSelect, selectedValues } = useSearchableSelect();
    const isSelected = selectedValues.includes(value);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pl-4 pr-2 text-sm outline-none text-accent-foreground', 'hover:bg-accent', className),
        onClick: ()=>{
            handleSelect(value);
        },
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "absolute left-0.5 flex size-3 items-center justify-center",
                children: isSelected && /*#__PURE__*/ _jsx(CheckIcon, {
                    className: "size-4"
                })
            }),
            children
        ]
    });
}
export function SearchableSelect(param) {
    let { options, value, onChange, placeholder = 'Select...', disabled = false, className, onOpenChange, renderValue, renderSearchInput, renderOptionsContent, size = 'default', multiple = false } = param;
    const [open, setOpen] = React.useState(false);
    const selectedOptions = React.useMemo(()=>{
        if (Array.isArray(value)) {
            return value.map((v)=>options.find((opt)=>opt.value === v)).filter((opt)=>Boolean(opt));
        }
        const found = options.find((opt)=>opt.value === value);
        return found ? [
            found
        ] : [];
    }, [
        value,
        options
    ]);
    const handleOpenChange = (isOpen)=>{
        setOpen(isOpen);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
    };
    const handleSelect = (selectedValue)=>{
        let newValue;
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : value ? [
                value
            ] : [];
            newValue = currentValues.includes(selectedValue) ? currentValues.filter((v)=>v !== selectedValue) : [
                ...currentValues,
                selectedValue
            ];
        } else {
            newValue = selectedValue;
            setOpen(false);
        }
        onChange(newValue);
    };
    const displayValue = React.useMemo(()=>{
        if (renderValue) {
            return renderValue(selectedOptions);
        }
        if (selectedOptions.length === 0) {
            return /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: placeholder
            });
        }
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center gap-1 flex-wrap",
            children: selectedOptions.map((option)=>/*#__PURE__*/ _jsx("span", {
                    className: cn('text-white text-base whitespace-nowrap overflow-hidden text-ellipsis inline-block', maxWidthDependsOnSize[size]),
                    children: option === null || option === void 0 ? void 0 : option.label
                }, option === null || option === void 0 ? void 0 : option.value))
        });
    }, [
        selectedOptions,
        renderValue,
        placeholder
    ]);
    return /*#__PURE__*/ _jsxs(SelectPrimitive.Root, {
        open: open,
        onOpenChange: handleOpenChange,
        children: [
            /*#__PURE__*/ _jsxs(SelectPrimitive.Trigger, {
                disabled: disabled,
                className: cn('flex w-full h-8 items-center justify-between border border-input bg-transparent px-3 py-2 min-h-10 text-sm shadow-xs cursor-pointer', 'focus:outline-none focus:ring-1 focus:ring-ring', 'disabled:cursor-not-allowed disabled:opacity-50', className),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 text-left",
                        children: displayValue
                    }),
                    /*#__PURE__*/ _jsx(ChevronDownIcon, {
                        className: "size-4 opacity-50 shrink-0 ml-2"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SelectPrimitive.Portal, {
                children: /*#__PURE__*/ _jsxs(SelectPrimitive.Content, {
                    className: cn('relative z-[1100] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md', 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', 'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'),
                    position: "popper",
                    sideOffset: 4,
                    align: "start",
                    children: [
                        renderSearchInput && /*#__PURE__*/ _jsx("div", {
                            className: "sticky top-0 z-10 bg-popover border-b",
                            children: renderSearchInput
                        }),
                        /*#__PURE__*/ _jsx(SelectPrimitive.Viewport, {
                            className: "p-1 max-h-[300px] overflow-y-auto bg-",
                            children: renderOptionsContent ? /*#__PURE__*/ _jsx(SearchableSelectContext.Provider, {
                                value: {
                                    handleSelect,
                                    selectedValues: Array.isArray(value) ? value : [
                                        value
                                    ]
                                },
                                children: renderOptionsContent
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    !!options.length && options.map((option)=>{
                                        const isSelected = Array.isArray(value) ? value.includes(option.value) : value === option.value;
                                        return /*#__PURE__*/ _jsxs("div", {
                                            className: cn('relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pl-4 pr-2 text-sm outline-none text-accent-foreground', 'hover:bg-accent', 'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'),
                                            onClick: ()=>{
                                                handleSelect(option.value);
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "absolute left-0 flex items-center justify-center",
                                                    children: isSelected && /*#__PURE__*/ _jsx(CheckIcon, {
                                                        className: "size-3"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value);
                                    }),
                                    !options.length && /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "No options found"
                                        })
                                    })
                                ]
                            })
                        })
                    ]
                })
            })
        ]
    });
}
